import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown, Ticket } from "lucide-react";
import { BOOK_URL, TICKETS_URL, M, EASE, scrollToId } from "../lib/site";
import { MaskLines } from "./Reveal";
import { Countdown } from "./Countdown";

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 60, damping: 15 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 60, damping: 15 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const vScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const lY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const lOp = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-black [perspective:1200px]">
      <motion.div style={{ scale: vScale }} className="absolute inset-0">
        <video className="h-full w-full object-cover" src={M.video} poster={M.poster}
          autoPlay muted loop playsInline preload="auto" data-testid="hero-video" />
      </motion.div>
      <div className="absolute inset-0 bg-black/60" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_10%,rgba(9,8,11,0.85)_75%)]" />
      <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-[#09080B] to-transparent" />

      <motion.div style={{ y: lY, opacity: lOp }} className="relative z-10 flex h-full flex-col items-center justify-center px-5 text-center">
        <motion.p initial={{ opacity: 0, letterSpacing: "0.8em" }} animate={{ opacity: 1, letterSpacing: "0.32em" }}
          transition={{ duration: 1.6, ease: EASE, delay: 0.2 }}
          className="font-mono-x mb-6 text-[11px] uppercase text-[var(--glow)] sm:text-xs">
          Grand Opening — Friday 13 November
        </motion.p>
        <h1 className="sr-only">Eleganté Melbourne</h1>

        <motion.div style={{ rotateX: rx, rotateY: ry }} className="[transform-style:preserve-3d]">
          <motion.img src={M.logo} alt="Eleganté Melbourne" data-testid="hero-logo"
            initial={{ clipPath: "inset(0 100% 0 0)", opacity: 0.2, filter: "blur(8px)" }}
            animate={{ clipPath: "inset(0 0% 0 0)", opacity: 1, filter: "blur(0px)" }}
            transition={{ duration: 2.2, ease: EASE, delay: 0.4 }}
            className="w-[82vw] max-w-[760px] drop-shadow-[0_0_40px_rgba(229,184,105,0.35)] [transform:translateZ(60px)]" />
        </motion.div>

        <MaskLines onLoad delay={1.4} as="p" lines={["RNB  ·  House  ·  Afro House", "Every Friday  ·  9PM til late"]}
          className="font-thin-caps mt-6 space-y-2 whitespace-pre text-[13px] leading-relaxed text-[#F8F6F0]/90 sm:text-base" />

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2.3, duration: 1, ease: EASE }}
          className="mt-8 flex w-full max-w-[340px] flex-col items-center gap-3 sm:mt-10 sm:w-auto sm:max-w-none sm:flex-row sm:gap-4">
          <a href={TICKETS_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-tickets-btn"
            className="btn-gold inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-3.5 text-[13px] font-semibold uppercase tracking-[0.2em] sm:w-auto sm:py-4">
            <Ticket size={16} /> Grand Opening Tickets
          </a>
          <a href={BOOK_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-book-bottomless-btn"
            className="btn-ghost inline-flex w-full items-center justify-center gap-2 rounded-full border-[var(--glow)] px-8 py-3.5 text-[13px] font-medium uppercase tracking-[0.2em] text-[var(--glow)] sm:w-auto sm:py-4">
            Book Bottomless <ArrowUpRight size={16} />
          </a>
          <button onClick={() => scrollToId("contact")} data-testid="hero-contact-btn"
            className="btn-ghost inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-3.5 text-[13px] font-medium uppercase tracking-[0.2em] text-[#F8F6F0] sm:w-auto sm:py-4">
            Contact Us
          </button>
        </motion.div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.6, duration: 1 }}
        className="absolute inset-x-0 bottom-6 z-10 mx-auto flex max-w-[1400px] items-end justify-between px-5 sm:bottom-10 sm:px-10">
        <div className="mx-auto sm:mx-0">
          <Countdown />
        </div>
        <button onClick={() => scrollToId("opening")} data-testid="hero-scroll-btn"
          className="hidden flex-col items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#F8F6F0]/70 sm:flex">
          Scroll <ArrowDown size={14} className="animate-bounce" />
        </button>
        <p className="hidden max-w-[300px] text-right font-mono-x text-[11px] uppercase leading-relaxed tracking-[0.2em] text-[#F8F6F0]/70 md:block">
          Level 1 / 412 Little Collins St<br />Melbourne VIC 3000
        </p>
      </motion.div>
    </section>
  );
};
